import { matches } from './report-view';

// Shared by Payroll Details (Payment) and (Charges): the APIs return one entry per inspector
// (keyed by user id) with `completions` and `inspections` keyed by transaction id. These helpers
// turn that into ordered lists for the redesigned cards; the totals stay the API's own.

export interface PayrollLine {
  date: string;      // "09/02/2026"
  jobId: string;     // report # (from job_id, or the "m-d-Y - jobid" date_job)
  raw: any;
}

export interface PayrollGroup {
  key: string;
  name: string;
  rate: string;
  completions: PayrollLine[];
  inspections: PayrollLine[];
  data: any;         // the API's entry: totals such as inspector_payment
}

function lines(section: any): PayrollLine[] {
  if (!section || typeof section !== 'object') {
    return [];
  }
  return Object.keys(section).sort((a, b) => +a - +b).map(key => {
    const row = section[key] || {};
    const parts = String(row.date_job || '').split(' - ');
    return {
      date: (parts[0] || '').replace(/-/g, '/'),
      jobId: row.job_id ? String(row.job_id) : (parts[1] || '').trim(),
      raw: row,
    };
  });
}

/** API object -> groups in user-id order (as the old keyvalue pipe showed them). */
export function payrollGroups(reportData: any): PayrollGroup[] {
  if (!reportData || typeof reportData !== 'object') {
    return [];
  }
  return Object.keys(reportData).sort((a, b) => +a - +b).map(key => {
    const data = reportData[key] || {};
    return {
      key,
      name: (data.user_full_name || '').trim() || 'No inspector',
      rate: data.commission_rate_text || '',
      completions: lines(data.completions),
      inspections: lines(data.inspections),
      data,
    };
  });
}

/** A card matches when the inspector or any of its lines matches (the card is shown whole). */
export function payrollGroupMatches(group: PayrollGroup, search: string): boolean {
  if (matches(search, [group.name])) {
    return true;
  }
  return group.completions.concat(group.inspections).some(l =>
    matches(search, [l.jobId, l.date, l.raw.description, l.raw.address]));
}

export function payrollLineCount(groups: PayrollGroup[]): number {
  return groups.reduce((n, g) => n + g.completions.length + g.inspections.length, 0);
}
